import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowDownRight, ArrowUpLeft, ExternalLink, Network, Search, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ExportButton } from "@/components/ExportButton";
import { inputClass, PanelFrame } from "@/components/Panel";
import { ItemPicker } from "@/components/pickers";
import { Badge, Card } from "@/components/ui";
import { itemDetailQuery, type ItemRow } from "@/features/items/api";
import { bomExplosionQuery, whereUsedTreeQuery, type ExplosionRow, type WhereUsedTreeRow } from "@/features/production/api";
import { BomTree, type TreeRow } from "@/features/production/BomTree";
import { ancestorsOf, buildTree, collapseBeyond, findNodes, levelColumns, type Tree } from "@/features/production/bomTree";
import { useActiveCompany } from "@/lib/activeCompany";
import { cn } from "@/lib/cn";
import type { ExportColumn } from "@/lib/exportXlsx";
import { todayIso, useFormat } from "@/lib/format";

// Manufacturing → BOM structure (PX-ENG-210, docs/design/production.md §12):
// the multi-level BOM as a horizontal tree — levels are columns, links run
// from each item to its components ("structure", bom_explosion) or to the
// items that use it ("where used", bom_where_used_tree). One RPC per view;
// the item, date, quantity, direction and selected card live in the URL.

interface Search {
  item?: string | undefined;
  date?: string | undefined;
  qty?: string | undefined;
  dir: "down" | "up";
  node?: string | undefined;
}

export const Route = createFileRoute("/_app/engineering/bom-structure")({
  validateSearch: (raw: Record<string, unknown>): Search => {
    const s: Search = { dir: raw.dir === "up" ? "up" : "down" };
    if (typeof raw.item === "string" && /^[0-9a-f-]{36}$/i.test(raw.item)) s.item = raw.item;
    if (typeof raw.date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw.date)) s.date = raw.date;
    const qty = typeof raw.qty === "number" ? String(raw.qty) : raw.qty;
    if (typeof qty === "string" && /^\d+(\.\d+)?$/.test(qty) && Number(qty) > 0) s.qty = qty;
    if (typeof raw.node === "string" && raw.node.length <= 2000) s.node = raw.node;
    return s;
  },
  component: BomStructurePage,
});

/** Auto-collapse deep structures so the first view stays readable. */
const OPEN_LIMIT = 300;

function fromExplosion(rows: ExplosionRow[]): TreeRow[] {
  return rows.map((r) => ({
    depth: r.depth,
    ordinal: r.depth === 0 ? "" : `${r.line_no ?? 0}`,
    itemId: r.item_id,
    code: r.item_code,
    description: r.item_description,
    procurement: r.procurement_type,
    uom: r.uom,
    uomDecimals: r.uom_decimals,
    qtyPer: r.depth === 0 ? null : r.qty_per,
    qty: r.qty,
    bomId: r.bom_id,
    bomVersion: r.bom_version,
    missingBom: r.missing_bom,
    cycle: r.cycle,
    backflush: r.issue_method === "backflush",
    leadTime: r.lead_time,
    cumLeadTime: r.cum_lead_time,
  }));
}

function fromWhereUsed(root: TreeRow, rows: WhereUsedTreeRow[]): TreeRow[] {
  return [
    root,
    ...rows.map((r) => ({
      depth: r.depth,
      ordinal: r.item_id,
      itemId: r.item_id,
      code: r.item_code,
      description: r.item_description,
      procurement: "manufacture",
      uom: "",
      uomDecimals: 0,
      qtyPer: r.qty_per,
      qty: null,
      bomId: r.bom_id,
      bomVersion: r.bom_version,
      missingBom: false,
      cycle: false,
      backflush: false,
      leadTime: null,
      cumLeadTime: null,
    })),
  ];
}

function BomStructurePage() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { company } = useActiveCompany();
  const setSearch = useCallback(
    (patch: Partial<Search>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  const date = search.date ?? todayIso();
  const [qtyText, setQtyText] = useState(search.qty ?? "1");
  const qty = search.qty ?? "1";
  useEffect(() => {
    const v = qtyText.trim().replace(",", ".");
    if (v === qty || !/^\d+(\.\d+)?$/.test(v) || Number(v) <= 0) return;
    const id = setTimeout(() => setSearch({ qty: v }), 250);
    return () => clearTimeout(id);
  }, [qtyText, qty, setSearch]);

  const itemQ = useQuery({ ...itemDetailQuery(search.item ?? ""), enabled: !!search.item });
  const item = itemQ.data;
  const picked: ItemRow | null = item
    ? {
        id: item.id,
        code: item.code,
        description: item.description,
        item_type: item.item_type,
        procurement_type: item.procurement_type,
        lot_controlled: item.lot_controlled,
        serial_controlled: item.serial_controlled,
        is_active: item.is_active,
        base_uom: item.base_uom ? { code: item.base_uom.code } : null,
      }
    : null;

  const down = useQuery({
    ...bomExplosionQuery(search.item ?? "", date, qty, company?.id ?? ""),
    enabled: !!search.item && !!company && search.dir === "down",
  });
  const up = useQuery({ ...whereUsedTreeQuery(search.item ?? "", date), enabled: !!search.item && search.dir === "up" });

  const treeRows = useMemo<TreeRow[]>(() => {
    if (search.dir === "down") return fromExplosion(down.data?.rows ?? []);
    if (!item || !up.data) return [];
    const root: TreeRow = {
      depth: 0,
      ordinal: "",
      itemId: item.id,
      code: item.code,
      description: item.description,
      procurement: item.procurement_type,
      uom: item.base_uom?.code ?? "",
      uomDecimals: item.base_uom?.decimals ?? 0,
      qtyPer: null,
      qty: null,
      bomId: null,
      bomVersion: null,
      missingBom: false,
      cycle: false,
      backflush: false,
      leadTime: null,
      cumLeadTime: null,
    };
    return fromWhereUsed(root, up.data.rows);
  }, [search.dir, down.data, up.data, item]);
  const tree = useMemo(() => buildTree(treeRows), [treeRows]);
  const truncated = search.dir === "down" ? !!down.data?.truncated : !!up.data?.truncated;
  const pending = !!search.item && (search.dir === "down" ? down.isPending : up.isPending || itemQ.isPending);
  const failed = search.dir === "down" ? down.error : up.error;

  // Collapsed branches: reset per tree; large trees open two levels.
  const [collapsed, setCollapsed] = useState<{ tree: Tree<TreeRow>; keys: Set<string> } | null>(null);
  const collapsedKeys = useMemo(() => {
    if (collapsed?.tree === tree) return collapsed.keys;
    return tree.order.length > OPEN_LIMIT ? collapseBeyond(tree, 2) : new Set<string>();
  }, [collapsed, tree]);
  const setCollapsedKeys = (keys: Set<string>) => setCollapsed({ tree, keys });

  // Find in the tree: hits are highlighted, the way to the current hit is opened.
  const [term, setTerm] = useState("");
  const [hitIndex, setHitIndex] = useState(0);
  const hits = useMemo(() => findNodes(tree, term, (r) => `${r.code} ${r.description}`), [tree, term]);
  const current = hits.length ? hits[hitIndex % hits.length] : undefined;
  const goToHit = (i: number) => {
    const key = hits[(i + hits.length) % hits.length];
    if (!key) return;
    setHitIndex((i + hits.length) % hits.length);
    const open = new Set(collapsedKeys);
    for (const a of ancestorsOf(tree, key)) open.delete(a);
    setCollapsedKeys(open);
  };

  const selectedNode = search.node ? tree.nodes.get(search.node) : undefined;
  const levelCount = tree.maxDepth + 1;
  const exportColumns: ExportColumn<{ cells: string[]; row: TreeRow }>[] = [
    ...Array.from({ length: levelCount }, (_, i) => ({
      header: i === 0 ? t("bomTree.top") : t("bomTree.level", { n: i }),
      kind: "code" as const,
      value: (r: { cells: string[] }) => r.cells[i] || null,
    })),
    { header: t("stock.colDescription"), kind: "text", value: (r) => r.row.description },
    { header: t("bom.colQtyPer"), kind: "number", value: (r) => r.row.qtyPer, sum: false },
    ...(search.dir === "down"
      ? ([
          { header: t("bom.colQtyTotal"), kind: "number", value: (r) => r.row.qty, unit: (r) => r.row.uom },
          { header: t("items.colUom"), kind: "code", value: (r) => r.row.uom },
          { header: t("bom.colCumLeadTime"), kind: "integer", value: (r) => r.row.cumLeadTime, sum: false },
        ] satisfies ExportColumn<{ cells: string[]; row: TreeRow }>[])
      : []),
    { header: t("bom.colVersion"), kind: "integer", value: (r) => r.row.bomVersion, sum: false },
  ];
  const exportRows = useMemo(() => levelColumns(tree, (r) => r.code).map(({ cells, node }) => ({ cells, row: node.row })), [tree]);

  return (
    <div className="flex min-h-0 grow flex-col gap-4">
      <header className="flex flex-wrap items-end gap-3">
        <div className="flex grow flex-col gap-0.5">
          <nav aria-label="Breadcrumb" className="text-[13px] text-ink-muted">
            {t("nav.engineering.title")} <span aria-hidden="true">/</span> {t("nav.engineering.bomStructure")}
          </nav>
          <h1 className="text-[28px] font-semibold leading-9 tracking-[-0.02em]">{t("bomTree.title")}</h1>
        </div>
        <ExportButton
          fileBase={`bom-structure-${item?.code ?? "item"}`}
          sheet={t("bomTree.title")}
          columns={exportColumns}
          disabled={exportRows.length === 0}
          fetchPage={async () => ({ rows: exportRows, nextCursor: null })}
        />
      </header>

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-full max-w-sm">
          <ItemPicker
            key={picked?.id ?? "none"}
            value={picked}
            onChange={(i) => setSearch({ item: i?.id, node: undefined })}
            label={t("bomTree.item")}
            {...(search.dir === "down" ? { filter: (i: ItemRow) => i.procurement_type === "manufacture" } : {})}
          />
        </div>
        <div role="group" aria-label={t("bomTree.direction")} className="flex rounded-xl border border-border bg-surface p-0.5">
          {(["down", "up"] as const).map((d) => (
            <button
              key={d}
              type="button"
              aria-pressed={search.dir === d}
              onClick={() => setSearch({ dir: d, node: undefined })}
              className={cn(
                "inline-flex h-8 items-center gap-1.5 rounded-lg px-3 text-[13px] font-medium",
                search.dir === d ? "bg-ink text-surface" : "text-ink-soft hover:bg-surface-hover",
              )}
            >
              {d === "down" ? <ArrowDownRight size={14} aria-hidden="true" /> : <ArrowUpLeft size={14} aria-hidden="true" />}
              {d === "down" ? t("bomTree.structure") : t("bomTree.whereUsed")}
            </button>
          ))}
        </div>
        <label className="flex flex-col gap-1 text-xs text-ink-muted">
          {t("bom.explodeDate")}
          <input type="date" value={date} onChange={(e) => e.target.value && setSearch({ date: e.target.value })} className={cn(inputClass, "h-9 w-40")} />
        </label>
        {search.dir === "down" && (
          <label className="flex flex-col gap-1 text-xs text-ink-muted">
            {t("bom.explodeQty")}
            <input value={qtyText} onChange={(e) => setQtyText(e.target.value)} inputMode="decimal" className={cn(inputClass, "tabular h-9 w-24 text-right")} />
          </label>
        )}
        <div className="grow" />
        <label className="flex h-9 min-w-[220px] items-center gap-2 rounded-xl border border-border bg-surface px-3 text-ink-muted focus-within:outline-2 focus-within:outline-focus">
          <Search size={15} aria-hidden="true" />
          <input
            value={term}
            onChange={(e) => {
              setTerm(e.target.value);
              setHitIndex(0);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                goToHit(e.shiftKey ? hitIndex - 1 : term === "" ? 0 : hitIndex + (current ? 1 : 0));
              }
            }}
            placeholder={t("bomTree.find")}
            aria-label={t("bomTree.find")}
            className="min-w-0 grow bg-transparent text-[13px] text-ink outline-none placeholder:text-ink-muted"
          />
          {term && (
            <>
              <span className="tabular text-xs" role="status">
                {hits.length ? `${(hitIndex % hits.length) + 1}/${hits.length}` : "0"}
              </span>
              <button type="button" aria-label={t("list.clearFilters")} onClick={() => setTerm("")}>
                <X size={14} />
              </button>
            </>
          )}
        </label>
      </div>

      <div className="flex min-h-0 grow gap-4">
        <Card aria-label={t("bomTree.title")} className="flex min-h-[480px] min-w-0 grow flex-col overflow-hidden">
          {!search.item ? (
            <Empty title={t("bomTree.pickTitle")} body={t("bomTree.pickBody")} />
          ) : pending ? (
            <div aria-hidden="true" className="flex grow items-center gap-16 p-8">
              {[3, 5, 4].map((n, c) => (
                <div key={c} className="flex flex-col gap-3">
                  {Array.from({ length: n }, (_, i) => (
                    <div key={i} className="h-14 w-56 animate-pulse rounded-xl bg-surface-muted" />
                  ))}
                </div>
              ))}
            </div>
          ) : failed ? (
            <p role="alert" className="p-6 text-[13px] text-(--status-danger-fg)">
              {(failed as Error).message}
            </p>
          ) : tree.order.length <= 1 ? (
            <Empty
              title={search.dir === "down" ? t("bomTree.noBomTitle") : t("bomTree.notUsedTitle")}
              body={search.dir === "down" ? t("bomTree.noBomBody") : t("bomTree.notUsedBody")}
            />
          ) : (
            <>
              <p className="border-b border-border-subtle px-3 py-1.5 text-xs text-ink-muted">
                {t("bomTree.summary", { items: tree.order.length - 1, count: tree.maxDepth })}
                {search.dir === "down" && tree.rootKey && tree.nodes.get(tree.rootKey)!.row.cumLeadTime !== null
                  ? ` · ${t("bomTree.leadTimeSummary", { n: tree.nodes.get(tree.rootKey)!.row.cumLeadTime ?? 0 })}`
                  : ""}
                {truncated ? ` · ${t("bom.truncated")}` : ""}
              </p>
              <BomTree
                tree={tree}
                direction={search.dir}
                collapsed={collapsedKeys}
                onCollapsedChange={setCollapsedKeys}
                selected={search.node}
                onSelect={(k) => setSearch({ node: k })}
                highlight={new Set(hits)}
                focusKey={current}
              />
            </>
          )}
        </Card>
        {selectedNode && (
          <aside aria-label={t("bomTree.detailLabel")} className="hidden w-[320px] shrink-0 flex-col rounded-2xl border border-border bg-surface lg:flex">
            <NodeDetail row={selectedNode.row} depth={selectedNode.depth} direction={search.dir} onClose={() => setSearch({ node: undefined })} onRoot={(id, dir) => setSearch({ item: id, dir, node: undefined })} />
          </aside>
        )}
      </div>
    </div>
  );
}

function Empty({ title, body }: { title: string; body: string }) {
  return (
    <div className="flex grow flex-col items-center justify-center gap-2 p-10 text-center">
      <Network size={28} className="text-ink-faint" aria-hidden="true" />
      <p className="font-medium">{title}</p>
      <p className="max-w-md text-[13px] text-ink-muted">{body}</p>
    </div>
  );
}

function NodeDetail({
  row,
  depth,
  direction,
  onClose,
  onRoot,
}: {
  row: TreeRow;
  depth: number;
  direction: "down" | "up";
  onClose: () => void;
  onRoot: (itemId: string, dir: "down" | "up") => void;
}) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const fact = (label: string, value: React.ReactNode) => (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs text-ink-muted">{label}</dt>
      <dd className="tabular text-[13px]">{value}</dd>
    </div>
  );
  return (
    <PanelFrame eyebrow={row.code} title={row.description} onClose={onClose}>
      <div className="flex flex-wrap gap-1.5">
        <Badge tone={row.procurement === "manufacture" ? "violet" : "info"}>{row.procurement === "manufacture" ? t("bomTree.make") : t("bomTree.buy")}</Badge>
        {row.missingBom && <Badge tone="warning">{t("bom.missingBom")}</Badge>}
        {row.cycle && <Badge tone="danger">{t("bom.cycle")}</Badge>}
        {row.backflush && <Badge tone="neutral">{t("bomTree.backflush")}</Badge>}
      </div>
      <dl className="grid grid-cols-2 gap-3">
        {fact(t("bomTree.levelLabel"), depth)}
        {row.bomVersion !== null && fact(t("bom.colVersion"), `v${row.bomVersion}`)}
        {depth > 0 && row.qtyPer !== null && fact(direction === "down" ? t("bomTree.qtyPerParent") : t("bomTree.qtyPerChild"), fmt.qty(String(row.qtyPer)))}
        {direction === "down" && row.qty !== null && fact(t("bomTree.qtyTotal"), `${fmt.qtyIn(row.qty, row.uomDecimals)} ${row.uom}`)}
        {row.leadTime !== null && fact(t("bom.colLeadTime"), t("bom.days", { n: row.leadTime }))}
        {row.cumLeadTime !== null && fact(t("bom.colCumLeadTime"), t("bom.days", { n: row.cumLeadTime }))}
      </dl>
      <div className="flex flex-col gap-2">
        {row.bomId && (
          <Link
            to="/engineering/boms"
            search={{ status: "all", q: "", id: row.bomId }}
            className="inline-flex h-9 items-center justify-between rounded-xl border border-border px-3 text-[13px] font-medium hover:bg-surface-hover"
          >
            {t("bomTree.openBom", { v: row.bomVersion ?? "" })}
            <ExternalLink size={14} aria-hidden="true" />
          </Link>
        )}
        {depth > 0 && row.procurement === "manufacture" && (
          <button type="button" onClick={() => onRoot(row.itemId, "down")} className="inline-flex h-9 items-center gap-2 rounded-xl border border-border px-3 text-[13px] font-medium hover:bg-surface-hover">
            <ArrowDownRight size={14} aria-hidden="true" />
            {t("bomTree.showStructureOf", { code: row.code })}
          </button>
        )}
        <button type="button" onClick={() => onRoot(row.itemId, "up")} className="inline-flex h-9 items-center gap-2 rounded-xl border border-border px-3 text-[13px] font-medium hover:bg-surface-hover">
          <ArrowUpLeft size={14} aria-hidden="true" />
          {t("bomTree.showWhereUsedOf", { code: row.code })}
        </button>
        <Link
          to="/master-data/items"
          search={{ type: "all", q: "", inactive: false, item: row.itemId }}
          className="inline-flex h-9 items-center justify-between rounded-xl border border-border px-3 text-[13px] font-medium hover:bg-surface-hover"
        >
          {t("bomTree.openItem")}
          <ExternalLink size={14} aria-hidden="true" />
        </Link>
      </div>
    </PanelFrame>
  );
}
